/**
 * F-70 translate dialog — PURE state + wording (no React, no DOM, no Yjs).
 *
 * The dialog component owns a `TranslateDialogState` in `useState` and asks
 * this module three questions: what does the form start as, may it submit, and
 * what does the user read when the run finishes or fails. Keeping all three
 * here is what makes them testable without mounting React.
 */
import { TRANSLATION_AUTO_LANG, type ColumnTranslation } from "@kola/shared";

import { ApiError } from "../../core/api.js";

/** The dialog's form state: the (from, to) pair and the F-70.2 trigger. */
export interface TranslateDialogState {
  /** A language tag, or `TRANSLATION_AUTO_LANG` — never empty (auto is the default). */
  from: string;
  /** A language tag; "" = not chosen yet (F-70: To is required). */
  to: string;
  /**
   * F-70.2's two triggers, as one flag:
   *  - false = "Translate empty rows" — the DEFAULT, and a resume. Rows that
   *    already hold a current machine translation are skipped at PLAN time, so
   *    a re-run after a cancel or a crash continues instead of re-buying the
   *    whole column ("resumable by construction").
   *  - true  = "Re-translate" — re-send those rows too.
   * Either way a human-edited target is never overwritten (C5.1/F-70.5).
   */
  retranslate: boolean;
}

/**
 * The form as it opens. F-70: From defaults to auto-detect, To is required and
 * has no default — EXCEPT when this column already carries a mapping, in which
 * case both pre-fill from it. That is what makes the same dialog the
 * "re-translate" affordance: open it on a translated column and it already says
 * what the column holds, so Translate re-runs the same pair.
 */
export function initialDialogState(mapping: ColumnTranslation | undefined): TranslateDialogState {
  return {
    from: mapping?.from ?? TRANSLATION_AUTO_LANG,
    to: mapping?.to ?? "",
    // Always RESUME on open, even for a column that already has a mapping: the
    // expensive, destructive-looking option is never the one a stray Enter
    // picks.
    retranslate: false,
  };
}

/** Why Translate is not submittable, or null when it is. */
export type SubmitBlock =
  /** F-70: To is required. */
  | "no-target"
  /**
   * From and To are the same language. The provider would happily bill us to
   * copy the column, so the dialog refuses it. "auto" never collides — the
   * whole point is that the source language is unknown here.
   */
  | "same-language";

/** Null = submittable. */
export function submitBlock(state: TranslateDialogState): SubmitBlock | null {
  if (state.to === "") return "no-target";
  if (state.from !== TRANSLATION_AUTO_LANG && state.from === state.to) return "same-language";
  return null;
}

export function canSubmit(state: TranslateDialogState): boolean {
  return submitBlock(state) === null;
}

/** The inline message under the form for a blocked submit. */
export function submitBlockMessage(block: SubmitBlock): string {
  switch (block) {
    case "no-target":
      return "Choose a language to translate into.";
    case "same-language":
      return "Choose a target language different from the source.";
  }
}

/**
 * A failed run, in the user's words. Every branch answers "what do I do now?",
 * because a translate that silently does nothing is the worst outcome of this
 * feature: 503 means an admin has to add the key, 409 means wait, 400
 * no-source-column means this board predates F-28.
 *
 * Anything unrecognized (a 500, a dropped connection — `fetch` rejects with a
 * TypeError, not an ApiError) degrades to a generic retry message rather than
 * leaking a stack trace into a toast.
 */
export function translateErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "translation-not-configured":
        return "Translation isn't set up on this server yet — ask an admin to add the OpenRouter API key.";
      case "already-running":
        // Per BOARD, not per column: the server allows one live run per board so
        // that the doc's single `txJob` progress key has exactly one writer.
        return "A translation is already running on this board. Wait for it to finish, then try again.";
      case "forbidden":
        return "You don't have permission to edit this board.";
      case "not-found":
        return "This board no longer exists.";
      case "no-source-column":
        return 'This board has no "Original Script" column to translate from.';
      case "target-is-source":
        return '"Original Script" is the source column — it cannot be a translation target.';
      case "unknown-column":
        return "That column no longer exists.";
      case "unknown-sheet":
        return "That sheet no longer exists.";
      case "provider-failed":
        return "The translation provider rejected the request — ask an admin to check the OpenRouter key.";
      case "rate-limited":
        return "You've run a lot of translations recently. Wait a few minutes and try again.";
      case "too-many-concurrent":
        return "You already have a translation running. Wait for it to finish, then try again.";
      case "too-many-rows":
        return "This column has too many rows to translate in one run — ask an admin to raise the limit.";
      // The next two are unreachable from the dialog (it only submits curated
      // languages and never sends a model), so they mean a stale client or a
      // hand-made request. Say something true rather than leak a status code.
      case "unknown-language":
        return "That language isn't supported. Pick one from the list.";
      case "model-not-allowed":
        return "That translation model isn't allowed on this server.";
      // The cell request (cell-translate.ts, POST …/translate/cells).
      case "unusable-response":
        return "The model's answer didn't line up with the cells — try again.";
      case "too-long":
        return "That's too much text for one go — select fewer cells.";
      case "duplicate-cell":
        return "The same cell was sent twice — reselect and try again.";
      case "upstream-unauthorized":
      case "upstream-payment-required":
        return "The translation provider rejected the request — ask an admin to check the OpenRouter key.";
      case "upstream-model-rejected":
        return "The translation model refused the request — ask an admin to check the model setting.";
      case "upstream-rate-limited":
        return "The translation provider is rate-limiting us. Wait a minute and try again.";
      case "upstream-timeout":
      case "upstream-unreachable":
        return "The translation provider didn't answer — try again in a moment.";
      default:
        return `Translation failed (${error.status}). Try again.`;
    }
  }
  return "Translation failed — check your connection and try again.";
}
